import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { DragDropTable, TableColumn, SortConfig } from '../../components/ui/drag-drop-table';
import { EnhancedPagination } from '../../components/ui/enhanced-pagination';
import { useUIStore } from '../../stores/ui-store';
import { TemplateForm } from './TemplateForm';
import { ApiErrorFallback } from '../../components/common/ApiErrorFallback';
import { LoadingState } from '../../components/common/LoadingState';
import { EmptyState } from '../../components/common/EmptyState';
import {
  usePlatformTemplateList,
  useDeletePlatformTemplate,
  useReactivatePlatformTemplate,
  useReorderPlatformTemplates,
  useExportPlatformTemplatesCsv,
  useImportPlatformTemplatesCsv
} from './api';
import {
  PlatformTemplateSummaryResponse,
  getTemplateScopeName,
  getTemplateStructureName,
  getDifficultyLevelName,
  TemplateScope,
  TemplateStructure,
  DifficultyLevel
} from './types';

export function PlatformTemplatesPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState<DifficultyLevel | undefined>();
  const [selectedScope, setSelectedScope] = useState<TemplateScope | undefined>();
  const [selectedStructure, setSelectedStructure] = useState<TemplateStructure | undefined>();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PlatformTemplateSummaryResponse | undefined>();
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'name', direction: 'asc' });

  const [searchParams, setSearchParams] = useSearchParams();
  const { addNotification } = useUIStore();
  const deleteMutation = useDeletePlatformTemplate();
  const reactivateMutation = useReactivatePlatformTemplate();
  const reorderMutation = useReorderPlatformTemplates();
  const exportCsvMutation = useExportPlatformTemplatesCsv();
  const importCsvMutation = useImportPlatformTemplatesCsv();

  // Fetch templates list with current filters
  const { data, isLoading, error, refetch } = usePlatformTemplateList({
    page: currentPage,
    pageSize: pageSize,
    search: searchTerm || undefined,
    difficulty: selectedDifficulty,
    scope: selectedScope,
    structure: selectedStructure,
    includeInactive,
    sortBy: sortConfig.field,
    sortDirection: sortConfig.direction,
  });

  const handleCreateNew = () => {
    setEditingTemplate(undefined);
    setFormMode('create');
    setIsFormOpen(true);
  };

  // Handle URL parameters for quick actions
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'new') {
      handleCreateNew();
      // Clean up the URL parameter
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleEdit = (template: PlatformTemplateSummaryResponse) => {
    setEditingTemplate(template);
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const handleDelete = async (template: PlatformTemplateSummaryResponse) => {
    if (!window.confirm(`Are you sure you want to deactivate "${template.name}"?`)) {
      return;
    }

    try {
      await deleteMutation.mutateAsync({ id: template.id });
      addNotification({
        type: 'success',
        title: 'Template Deactivated',
        message: `${template.name} has been deactivated successfully.`,
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to Deactivate Template',
        message: error.message || 'An unexpected error occurred.',
      });
    }
  };

  const handleReactivate = async (template: PlatformTemplateSummaryResponse) => {
    try {
      await reactivateMutation.mutateAsync({ id: template.id });
      addNotification({
        type: 'success',
        title: 'Template Reactivated',
        message: `${template.name} has been reactivated successfully.`,
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to Reactivate Template',
        message: error.message || 'An unexpected error occurred.',
      });
    }
  };

  const handleReorder = async (reorderedTemplates: PlatformTemplateSummaryResponse[]) => {
    const templateIds = reorderedTemplates.map(template => template.id);
    
    try {
      await reorderMutation.mutateAsync({ templateIds });
      addNotification({
        type: 'success',
        title: 'Templates Reordered',
        message: 'Template order has been updated successfully.',
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to Reorder Templates',
        message: error.message || 'An unexpected error occurred.',
      });
      // Refetch to restore original order
      refetch();
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    refetch();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDifficulty(undefined);
    setSelectedScope(undefined);
    setSelectedStructure(undefined);
    setIncludeInactive(false);
    setCurrentPage(1);
  };

  const handleCsvExport = async () => {
    try {
      const blob = await exportCsvMutation.mutateAsync({
        includeInactive,
        search: searchTerm || undefined,
        difficulty: selectedDifficulty,
        scope: selectedScope,
        structure: selectedStructure,
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `platform_templates_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Export Failed',
        message: error.message || 'Failed to export template data.',
      });
    }
  };

  const handleCsvImport = async (file: File) => {
    const result = await importCsvMutation.mutateAsync({ file });
    return {
      created: result.summary.created,
      updated: result.summary.updated,
      skipped: result.summary.skipped,
      errors: result.errors,
    };
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      addNotification({
        type: 'error',
        title: 'Invalid File Type',
        message: 'Please select a CSV file.',
      });
      return;
    }

    try {
      const result = await handleCsvImport(file);
      
      addNotification({
        type: 'success',
        title: 'Import Successful',
        message: `Created: ${result.created}, Updated: ${result.updated}, Skipped: ${result.skipped}`,
      });

      if (result.errors.length > 0) {
        console.warn('Import warnings:', result.errors);
        addNotification({
          type: 'warning',
          title: 'Import Warnings',
          message: `${result.errors.length} items had issues. Check console for details.`,
        });
      }
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Import Failed',
        message: error.message || 'An unexpected error occurred during import.',
      });
    } finally {
      // Reset file input
      const input = document.getElementById('csv-import-input') as HTMLInputElement;
      if (input) {
        input.value = '';
      }
    }
  };

  const handleSort = (field: string) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const getSessionTypeDescription = (template: PlatformTemplateSummaryResponse): string => {
    const parts = [];
    parts.push(getTemplateStructureName(template.structure));
    parts.push(getDifficultyLevelName(template.difficulty));
    if (template.durationMinutes) {
      parts.push(`${template.durationMinutes} min`);
    }
    return parts.join(' • ');
  };

  // Define table columns for the drag and drop table
  const columns: TableColumn<PlatformTemplateSummaryResponse>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (template) => (
        <div>
          <div className="font-medium text-gray-900">{template.name}</div>
          {template.tags && (
            <div className="text-xs text-gray-500 mt-1">
              {template.tags}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      render: (template) => (
        template.description ? (
          <div className="text-sm text-gray-700 max-w-xs">
            {template.description.length > 80
              ? `${template.description.substring(0, 80)}...`
              : template.description}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">No description</span>
        )
      ),
    },
    {
      key: 'sessionType',
      label: 'Session Type',
      sortable: true,
      render: (template) => (
        <div>
          <div className="text-sm text-gray-700">
            {getSessionTypeDescription(template)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              template.scope === TemplateScope.Global ? 'bg-purple-100 text-purple-800' :
              template.scope === TemplateScope.Studio ? 'bg-blue-100 text-blue-800' :
              'bg-green-100 text-green-800'
            }`}>
              {getTemplateScopeName(template.scope)}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'content',
      label: 'Content',
      sortable: true,
      render: (template) => (
        <div>
          <div className="text-sm text-gray-700">
            {template.blockCount > 0 && (
              <div>{template.blockCount} block{template.blockCount !== 1 ? 's' : ''}</div>
            )}
            {template.exerciseCount > 0 && (
              <div>{template.exerciseCount} exercise{template.exerciseCount !== 1 ? 's' : ''}</div>
            )}
            {template.blockCount === 0 && template.exerciseCount === 0 && (
              <span className="text-gray-400">Empty template</span>
            )}
          </div>
          {template.maxParticipants && (
            <div className="text-xs text-gray-500 mt-1">
              Max {template.maxParticipants} participants
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (template) => (
        template.isActive ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Active
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Inactive
          </span>
        )
      ),
    },
  ];

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Platform Session Templates</h1>
        </div>
        <ApiErrorFallback 
          error={error}
          onRetry={() => refetch()}
          title="Unable to load templates"
          description="There was a problem loading the session templates list."
          showDetails={true}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Session Templates</h1>
          <p className="text-gray-600 text-sm">
            Manage reusable session templates available to all studios
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={handleCsvExport}
            variant="outline"
            size="sm"
            disabled={exportCsvMutation.isPending}
            className="flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>{exportCsvMutation.isPending ? 'Exporting...' : 'Export CSV'}</span>
          </Button>
          <Button
            onClick={() => document.getElementById('csv-import-input')?.click()}
            variant="outline"
            size="sm"
            disabled={importCsvMutation.isPending}
            className="flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            <span>{importCsvMutation.isPending ? 'Importing...' : 'Import CSV'}</span>
          </Button>
          <Button onClick={handleCreateNew}>
            Create New Template
          </Button>
        </div>
      </div>

      {/* Hidden file input for CSV import */}
      <input
        id="csv-import-input"
        type="file"
        accept=".csv"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Filters */}
      <Card>
        <CardContent className="py-3">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-2 items-center">
            <div className="flex-1 min-w-[200px]">
              <Input
                id="search"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search templates..."
                className="h-8 text-sm"
              />
            </div>
            
            <select
              id="difficulty"
              value={selectedDifficulty || ''}
              onChange={(e) => setSelectedDifficulty(e.target.value ? parseInt(e.target.value) as DifficultyLevel : undefined)}
              className="h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[110px]"
            >
              <option value="">All Difficulties</option>
              {Object.values(DifficultyLevel)
                .filter(value => typeof value === 'number')
                .map((difficulty) => (
                  <option key={difficulty} value={difficulty}>
                    {getDifficultyLevelName(difficulty as DifficultyLevel)}
                  </option>
                ))}
            </select>

            <select
              id="scope"
              value={selectedScope || ''}
              onChange={(e) => setSelectedScope(e.target.value ? parseInt(e.target.value) as TemplateScope : undefined)}
              className="h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[100px]"
            >
              <option value="">All Scopes</option>
              {Object.values(TemplateScope)
                .filter(value => typeof value === 'number')
                .map((scope) => (
                  <option key={scope} value={scope}>
                    {getTemplateScopeName(scope as TemplateScope)}
                  </option>
                ))}
            </select>

            <select
              id="structure"
              value={selectedStructure || ''}
              onChange={(e) => setSelectedStructure(e.target.value ? parseInt(e.target.value) as TemplateStructure : undefined)}
              className="h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px]"
            >
              <option value="">All Structures</option>
              {Object.values(TemplateStructure)
                .filter(value => typeof value === 'number')
                .map((structure) => (
                  <option key={structure} value={structure}>
                    {getTemplateStructureName(structure as TemplateStructure)}
                  </option>
                ))}
            </select>

            <div className="flex items-center space-x-1">
              <input
                id="includeInactive"
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="includeInactive" className="text-xs text-gray-700 whitespace-nowrap">
                Inactive
              </label>
            </div>

            <div className="flex space-x-1">
              <Button type="submit" variant="outline" size="sm" className="h-8 px-3">
                Search
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-8 px-3" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>


      {/* Templates Table */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <LoadingState message="Loading templates..." />
          ) : !data?.templates.length ? (
            <EmptyState
              title="No templates found"
              description={
                searchTerm || selectedDifficulty || selectedScope || selectedStructure || includeInactive
                  ? 'Try adjusting your filters or create new templates.'
                  : 'Get started by creating your first session template.'
              }
              actionLabel="Create New Template"
              onAction={handleCreateNew}
              showCard={false}
            />
          ) : (
            <>
              {/* Top Pagination - Compact */}
              {data.pagination && (
                <div className="flex justify-between items-center mb-4 py-2 border-b border-gray-100">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span>
                      Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, data.pagination.totalCount)} of {data.pagination.totalCount}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                      className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={10}>10 per page</option>
                      <option value={15}>15 per page</option>
                      <option value={25}>25 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage <= 1 || isLoading}
                        className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="px-2 py-1 text-sm">
                        {currentPage} of {data.pagination.totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(Math.min(data.pagination.totalPages, currentPage + 1))}
                        disabled={currentPage >= data.pagination.totalPages || isLoading}
                        className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <DragDropTable
                items={data.templates}
                columns={columns}
                keyExtractor={(template) => template.id}
                onReorder={handleReorder}
                isLoading={reorderMutation.isPending}
                sortConfig={sortConfig}
                onSort={handleSort}
                renderActions={(template) => (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(template)}
                    >
                      Edit
                    </Button>
                    {template.isActive ? (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(template)}
                        disabled={deleteMutation.isPending}
                      >
                        {deleteMutation.isPending ? 'Deactivating...' : 'Deactivate'}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleReactivate(template)}
                        disabled={reactivateMutation.isPending}
                      >
                        {reactivateMutation.isPending ? 'Reactivating...' : 'Reactivate'}
                      </Button>
                    )}
                  </>
                )}
              />

              {/* Enhanced Pagination */}
              {data.pagination && (
                <EnhancedPagination
                  pagination={data.pagination}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={handlePageSizeChange}
                  isLoading={isLoading}
                  className="mt-4"
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Template Form Modal */}
      <TemplateForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        template={editingTemplate}
        mode={formMode}
      />
    </div>
  );
}